/**
 * Where a world sound sits in the stereo field. Diablo II's sound system pans by the source's
 * horizontal screen offset from the listener and attenuates by its distance, with the reach set
 * by the row's Sounds.txt `Falloff` class. The engine compiles the reach per class in; these
 * ranges (in subtiles) are tuned so a normal sound fades out just past the screen edge, ambient
 * events and speech carry further and thunder is heard everywhere.
 */

/** Audible radius in subtiles per Falloff class (0 = never attenuated). */
export const FALLOFF_RANGE: readonly number[] = [0, 36, 60, 200, 60];
/** Fraction of the radius heard at full volume. */
const INNER = 0.2;
/** Pixels from screen center to edge at 800×600. */
const HALF_VIEW_W = 400;

export interface Placement {
  volume: number;
  /** -1 (left) .. 1 (right). */
  pan: number;
}

/**
 * Volume and pan for a source `dx, dy` subtiles from the listener. `separation` (0..1, the
 * Sound Options "3D bias") scales how far sounds spread across the speakers.
 */
export function spatialize(dx: number, dy: number, falloff: number, separation = 1): Placement {
  const screenX = (dx - dy) * 16;
  const pan = Math.max(-1, Math.min(1, screenX / HALF_VIEW_W)) * separation;
  const range = FALLOFF_RANGE[falloff] ?? FALLOFF_RANGE[1];
  if (!range) return { volume: 1, pan };
  const d = Math.hypot(dx, dy);
  const inner = range * INNER;
  const lin = d <= inner ? 1 : Math.max(0, 1 - (d - inner) / (range - inner));
  // Squared ramp: roughly linear in decibels, like DirectSound's attenuation.
  return { volume: lin * lin, pan };
}
